/**
 * Initial app shell — keep imports minimal (heavy UI/services belong in lazy routes / feature modules).
 * See docs/architecture/frontend-bundling.md.
 */
import { BrowserRouter } from 'react-router-dom'
import { Suspense } from 'react'
import { Provider } from 'react-redux'
import { QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from '@/components/notifications/toaster'
import { FloatingSupportButton } from '@/components/layout/FloatingSupportButton'
import { UIPreferencesProvider } from '@/contexts/UIPreferencesContext'
import { TableViewPreferencesProvider } from '@/contexts/TableViewPreferencesContext'
import { ThemeProvider } from '@/lib/theme'
import { SidebarVisibilityProvider } from '@/contexts/SidebarVisibilityContext'
import { store } from './app/store'
import { queryClient } from './app/queryClient'
import { PageLoader } from './components/loaders/PageLoader'
import { AuthInitializer } from './components/auth/AuthInitializer'
import { PolicySync } from './components/auth/PolicySync'
import { AppLockGuard } from './components/auth/AppLockGuard'
import AppRoutes from './routes'

// Dev-only: expose notification debug utilities
if (import.meta.env.DEV) {
  import('./utils/notification-debug.util')
}

function App() {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <UIPreferencesProvider>
          <TableViewPreferencesProvider>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <ThemeProvider>
              <SidebarVisibilityProvider>
                <AppLockGuard>
                  <AuthInitializer>
                    <PolicySync />
                    <Suspense fallback={<PageLoader />}>
                      <AppRoutes />
                    </Suspense>
                  </AuthInitializer>
                </AppLockGuard>
                <FloatingSupportButton />
              </SidebarVisibilityProvider>
              <Toaster />
            </ThemeProvider>
          </BrowserRouter>
          </TableViewPreferencesProvider>
        </UIPreferencesProvider>
      </QueryClientProvider>
    </Provider>
  )
}

export default App

